import {
    createRouter,
    createWebHistory,
    type RouteRecordRaw,
    type NavigationGuardNext,
    type RouteLocationNormalized,
} from 'vue-router';
import { AppProvider } from '@/components/layout';
import { ref } from 'vue';
import { isDingTalk } from '@/utils/tools';

const redirectOrigin = globalThis.location.origin;

const appKey = ref('ding9g1atyaovi62jcd0');

/** PC/浏览器钉钉登录 */
const buildDingUrl = (): string => {
    return `https://login.dingtalk.com/oauth2/auth?redirect_uri=${redirectOrigin}/login&response_type=code&client_id=${appKey.value}&scope=openid&prompt=consent`;
};

/** 钉钉内置浏览器登录 */
const buildDingUrlInDingTalk = (): string => {
    return `https://oapi.dingtalk.com/connect/oauth2/sns_authorize?appid=${appKey.value}&response_type=code&scope=snsapi_auth&state=STATE&redirect_uri=${redirectOrigin}/login`;
};
const routes: RouteRecordRaw[] = [
    {
        path: '/',
        name: 'auth',
        component: AppProvider,
        children: [
            {
                path: 'login',
                name: 'login',
                component: async () => import('@/views/auth/login.vue'),
                meta: {
                    inRoutes: true, // 标识路由在路由配置中
                },
            },
            {
                path: 'shell-otp',
                name: 'shell-otp',
                component: async () => import('@/views/webshell/index.vue'),
                meta: {
                    inRoutes: true, // 标识路由在路由配置中
                },
            },
        ],
    },
    /** 兜底：所有未知路由去 404 */
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: async () => import('@/views/error/not-found.vue'),
        meta: { inRoutes: true },
    },
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

/** 允许访问的页面（公网只保留这两个） */
const allowList = new Set<string>(['login', 'shell-otp', 'not-found']);

router.beforeEach((to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const name = String(to.name ?? '');

    /** 未匹配任何路由：直接去 404（观感更好） */
    if (to.matched.length === 0 || name === 'not-found') {
        next();
        return;
    }

    /** 非允许路由：直接拉回 shell-otp */
    if (!allowList.has(name)) {
        next({ name: 'shell-otp', replace: true });
        return;
    }

    /** 已登录（你后端写入的 token）：放行 */
    const token = localStorage.getItem('ACCESS-TOKEN');
    if (token) {
        next();
        return;
    }

    /** 未登录：login 和 shell-otp 都要能触发钉钉登录 */
    const hasPcCode = Boolean(to.query['authCode']);
    const hasDdCode = Boolean(to.query['code']);

    /** 如果是 login 页面且没有 code，就去钉钉 */
    if (name === 'login') {
        if (!isDingTalk() && !hasPcCode) {
            window.open(buildDingUrl(), '_self');
            return;
        }
        if (isDingTalk() && !hasDdCode) {
            window.open(buildDingUrlInDingTalk(), '_self');
            return;
        }
        next();
        return;
    }

    /** 如果是 shell-otp 页面且没有 code，也去钉钉，但先记住回跳参数 */
    if (name === 'shell-otp') {
        const redirectRoute = { name: 'shell-otp', query: { ...to.query } };
        localStorage.setItem('redirectRoute', JSON.stringify(redirectRoute));

        if (!isDingTalk() && !hasPcCode) {
            window.open(buildDingUrl(), '_self');
            return;
        }
        if (isDingTalk() && !hasDdCode) {
            window.open(buildDingUrlInDingTalk(), '_self');
            return;
        }
        next();
        return;
    }

    next();
});

export default router;
